import PageMeta from "../components/PageMeta";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LuLock } from "react-icons/lu";
import { allowsResidential } from "../utils/propertyRequestForm";
import { amount, area, categoryLabel, formatDate, placeLabel, rangeLabel, requestTitle } from "../components/propertyRequest/labels";
import { propertyRequestApi } from "../Services/propertyRequestService";

// Public detail (read only). The API never returns the owner's identity or contact details.
export default function PropertyRequestDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, error: "", request: null });

  useEffect(() => {
    let current = true;
    propertyRequestApi.get(id)
      .then((request) => { if (current) setState({ loading: false, error: "", request }); })
      .catch((e) => { if (current) setState({ loading: false, error: e.message, request: null }); });
    return () => { current = false; };
  }, [id]);

  const r = state.request;
  const none = t("propertyRequest.summary.none");
  const criteria = r && [
    ["location", placeLabel(t, r.city, r.delegations || [])],
    ["budget", rangeLabel(t, r.budgetMin, r.budgetMax, amount(t, r.transaction))],
    ["surface", rangeLabel(t, r.surfaceMin, r.surfaceMax, area)],
    ...(allowsResidential({ categories: r.categories || [] }) ? [
      ["bedrooms", r.bedroomsMin != null ? t("propertyRequest.summary.bedroomsValue", { count: r.bedroomsMin }) : none],
      ["attributes", r.attributes?.length ? r.attributes.map((a) => t(`propertyRequest.attribute.${a}`)).join(" · ") : none],
    ] : []),
  ];

  return (
    <main className="lina-property-request mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <PageMeta titleKey="seo.request" title={r ? requestTitle(t, r) : null} />
      <Link to="/property-requests" className="text-sm font-semibold text-green-800 underline">{t("propertyRequest.detail.back")}</Link>
      {state.loading && <p role="status" className="mt-6 text-slate-600">{t("propertyRequest.mine.loading")}</p>}
      {state.error && <p role="alert" className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{state.error}</p>}
      {r && (
        <article className="mt-4 space-y-5">
          <div className="flex flex-wrap gap-2">
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${r.status === "Active" ? "bg-green-700 text-white" : "bg-slate-200 text-slate-800"}`}>{t(`propertyRequest.status.${r.status}`)}</span>
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-800">{t(`propertyRequest.transactionShort.${r.transaction}`)}</span>
            {(r.categories || []).map((c) => <span key={c} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{categoryLabel(t, c, r.transaction)}</span>)}
          </div>
          <h1 className="break-words text-3xl font-black text-slate-900 sm:text-4xl">{requestTitle(t, r)}</h1>
          <p className="text-sm text-slate-600">{t("propertyRequest.detail.published", { date: formatDate(r.createdAt, i18n.language) })} · {t("propertyRequest.detail.expires", { date: formatDate(r.expiresAt, i18n.language) })}</p>
          <dl className="grid grid-cols-1 overflow-hidden rounded-3xl border border-slate-200 bg-white sm:grid-cols-2">
            {criteria.map(([key, value]) => (
              <div key={key} className="border-b border-slate-100 px-4 py-3 sm:px-5">
                <dt className="text-sm text-slate-500">{t(`propertyRequest.summary.${key}`)}</dt>
                <dd className="break-words text-base font-semibold text-slate-900">{value}</dd>
              </div>
            ))}
          </dl>
          {r.freeText && (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("propertyRequest.detail.description")}</h2>
              <p className="mt-2 whitespace-pre-line break-words text-base text-slate-900">{r.freeText}</p>
            </section>
          )}
          <p className="flex items-start gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700"><LuLock aria-hidden="true" className="mt-0.5 shrink-0 text-green-700" />{t("propertyRequest.detail.privacy")}</p>
          {r.status === "Active" && (
            <Link to={`/property-request/${r.id}/propose`} className="lina-action-primary h-12 w-full text-base sm:w-auto">{t("propertyRequest.propose.action")}</Link>
          )}
        </article>
      )}
    </main>
  );
}
